import {useTranslation} from '@/translations/i18n.tsx'
import {Access} from '../../types'
import {useDeleteAccess} from '../../queries/useDeleteAccess'
import {QUERY_KEYS} from '@/queryClient.ts'
import {toast} from '@/utilities/toast/toast'
import {useQueryClient} from '@tanstack/react-query'
import {DeleteModal} from '@/components/commons/delete-modal/DeleteModal'
import {useMemo} from 'react'
import {format} from 'date-fns'
import {eventTypeMapToLabel} from '@/features/agenda/utils'

export const DeleteAccessModal = ({
    access,
    onDeleteSuccess,
    onClose
}: {
    access: Access
    onDeleteSuccess?: () => void
    onClose: () => void
}) => {
    const queryClient = useQueryClient()
    const {t} = useTranslation()
    const deleteAccessMutation = useDeleteAccess(access.id, {
        onSuccess: async () => {
            await queryClient.invalidateQueries({queryKey: [QUERY_KEYS.ACCESSES]})
            await queryClient.invalidateQueries({queryKey: [QUERY_KEYS.ACCESSES_SIGNATURE_SHEETS]})
            onDeleteSuccess?.()
            toast.success(t('accesses:access_deleted_success'))
        }
    })
    const deniedParagraph = useMemo(() => {
        if (access.type == 'absence') {
            return t('accesses:cannot_delete_validated_absence_paragraph')
        }
        if (access.status == 'validated') {
            return t('accesses:cannot_delete_validated_access_paragraph')
        }
        if (access.status == 'excluded') {
            return t('accesses:cannot_delete_excluded_access_paragraph')
        }
        if (access.status == 'discarded') {
            return t('accesses:cannot_delete_discarded_access_paragraph')
        }
        return t('accesses:cannot_delete_validated_access_paragraph')
    }, [access.type, access.status])

    const deniedTitle = useMemo(() => {
        if (access.type == 'absence') {
            return t('accesses:cannot_delete_validated_absence_title')
        }
        return t('accesses:cannot_delete_access_title')
    }, [access.type])

    const accessToDeleteLabel = useMemo(() => {
        const accessType = access.event.eventConsultants?.[0]?.accessType?.accessType
            ? t(eventTypeMapToLabel[access.event.eventConsultants?.[0]?.accessType?.accessType])
            : ''
        if (access.type == 'absence') {
            return t('accesses:absence_to_delete_label', {
                accessType,
                date: format(access.event?.date, 'dd/MM/yyyy')
            })
        }
        return t('accesses:access_to_delete_label', {
            accessType,
            date: format(access.event?.date, 'dd/MM/yyyy')
        })
    }, [access.type, access.event?.eventConsultants?.[0]?.accessType?.accessType, access.event?.date])

    return (
        <DeleteModal
            onClose={onClose}
            paragraph1={t('commons:delete_paragraph_1')}
            deniedTitle={deniedTitle}
            deniedParagraph={deniedParagraph}
            title={access.type == 'absence' ? t('accesses:delete_absence_title') : t('accesses:delete_access_title')}
            canBeDeleted={access.status == 'to_validate'}
            fullName={accessToDeleteLabel}
            deleteMutation={deleteAccessMutation}
        />
    )
}
